// src/hooks/useSubscriptionForm.js

import { useState, useEffect, useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { API } from '../api/config';

const API_URL = `${API.ASSINATURAS}/subscribers`;

const PLAN_NAMES = {
    1: "Aventureiro Solo",
    2: "Dupla Dinâmica",
    4: "A Party Completa"
};

const PRICES = {
    1: { "1": 88,  "6": 480,  "12": 864  },
    2: { "1": 158, "6": 858,  "12": 1498 },
    4: { "1": 296, "6": 1496, "12": 2696 },
};

function formatPrice(price) {
    if (price === null || price === undefined) return "R$ --,--";
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
}

const formatPhoneForUI = (value) => {
    if (!value) return "";
    let clean = value.replace(/\D/g, "");
    if (clean.startsWith("55") && clean.length >= 12) clean = clean.substring(2);

    let formatted = clean;
    if (clean.length > 0) {
        formatted = `(${clean.substring(0, 2)}`;
        if (clean.length > 2) {
            formatted += `)${clean.substring(2, 7)}`;
            if (clean.length > 7) formatted += `-${clean.substring(7, 11)}`;
        }
    }
    return formatted;
};

const formatPhoneForApi = (phone) => {
    if (!phone) return "";
    const clean = phone.replace(/\D/g, '');
    return (clean.length >= 10 && clean.length <= 11) ? `55${clean}` : clean;
};

export function useSubscriptionForm(initialData = {}) {
    const { user, loading } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();

    const [numPessoasState, setNumPessoas] = useState(initialData.numPessoas || 1);
    const [duracaoState, setDuracao] = useState(initialData.duracao || 1);
    const [participants, setParticipants] = useState([]);
    const [price, setPrice] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Route protection
    useEffect(() => {
        if (!loading && user === null) {
            const intent = {
                redirectTo: location.pathname,
                prefillData: initialData
            };
            sessionStorage.setItem('conversion_intent', JSON.stringify(intent));
            navigate("/login");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, loading, navigate, location.pathname]);

    // Sync participants list with numPessoas
    useEffect(() => {
        if (!user || loading) return;

        setParticipants(prev => {
            const currentLength = prev.length;
            const targetLength = numPessoasState;

            const userProfilePhoneFormatted = formatPhoneForUI(user.telefone || user.Telefone || '');
            const nameParts = (user.name || '').trim().split(' ');

            const firstParticipant = currentLength > 0 ? prev[0] : {
                firstName: nameParts[0] || '',
                lastName:  nameParts.length > 1 ? nameParts.slice(1).join(' ') : '',
                email:     user.email || '',
                phone:     userProfilePhoneFormatted
            };

            if (targetLength > currentLength) {
                const newSlots = Array(targetLength - currentLength).fill({ firstName: '', lastName: '', email: '', phone: '' });
                return currentLength === 0
                    ? [firstParticipant, ...newSlots]
                    : [firstParticipant, ...prev.slice(1), ...newSlots];
            } else if (targetLength < currentLength) {
                return targetLength === 0 ? [] : [firstParticipant, ...prev.slice(1, targetLength)];
            } else {
                return currentLength === 0 ? [firstParticipant] : prev;
            }
        });
    }, [loading, numPessoasState, user]);

    // Price calculation
    useEffect(() => {
        const calculated = PRICES[numPessoasState]?.[duracaoState];
        setPrice(calculated !== undefined ? calculated : null);
    }, [numPessoasState, duracaoState]);

    const handleParticipantChange = useCallback((index, field, value) => {
        setParticipants(prev =>
            prev.map((p, i) =>
                i === index ? { ...p, [field]: field === 'phone' ? formatPhoneForUI(value) : value } : p
            )
        );
    }, []);

    const createSubscription = async () => {
        if (!user?.token) {
            alert("Sessão expirada. Por favor, faça login novamente.");
            navigate("/login");
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = participants.map(p => ({
                email:     p.email,
                nome:      p.firstName,
                sobrenome: p.lastName,
                telefone:  formatPhoneForApi(p.phone),
                plano:     PLAN_NAMES[numPessoasState],
                duracao:   duracaoState
            }));

            const response = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Sends the opaque session token so the backend can
                    // identify the caller if it ever needs to add auth to
                    // this endpoint. Currently unused by subscribers POST
                    // but good practice to include now.
                    'Authorization': `Bearer ${user.token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401) {
                alert("Sessão expirada. Por favor, faça login novamente.");
                navigate("/login");
                return;
            }

            if (!response.ok) {
                const errText = await response.text();
                throw new Error(errText || "Erro desconhecido na API.");
            }

            alert("Assinatura garantida com sucesso!");
            navigate("/usuario");

        } catch (error) {
            console.error("Subscription Submission Error:", error);
            alert(`Erro ao processar assinatura: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        numPessoas:            numPessoasState,
        duracao:               duracaoState,
        participants,
        price:                 formatPrice(price),
        handleNumPessoasChange: (e) => setNumPessoas(parseInt(e.target.value, 10)),
        handleDuracaoChange:    (e) => setDuracao(parseInt(e.target.value, 10)),
        setNumPessoasDirectly:  useCallback((v) => setNumPessoas(v), []),
        setDuracaoDirectly:     useCallback((v) => setDuracao(v), []),
        handleParticipantChange,
        createSubscription,
        isSubmitting
    };
}